"use client";

import { useMemo } from "react";
import Link from "next/link";
import {
  Gift,
  Heart,
  LogOut,
  ShoppingCart,
  UserRound,
  WalletCards,
} from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { ROUTES } from "@/src/constants/routes";
import { cartApi } from "@/src/features/cart/cartApi";
import { useGetProfileQuery } from "@/src/features/profile/profileApi";
import { wishlistApi } from "@/src/features/wishlist/wishlistApi";
import { useAppSelector } from "@/src/store/hooks";
import { selectHeaderBadges } from "@/src/store/selectors/headerSelectors";

type HeaderActionIconsProps = {
  variant?: "default" | "home";
};

type HeaderPointsPillProps = {
  className?: string;
};

const actions = [
  {
    href: ROUTES.LOGOUT,
    label: "تسجيل الخروج",
    icon: LogOut,
  },
  {
    href: "/profile",
    label: "الملف الشخصي",
    icon: UserRound,
  },
  
  {
    href: "/wishlist",
    label: "المفضلة",
    icon: Heart,
    badge: 2,
  },
  {
    href: "/cart",
    label: "عربة التسوق",
    icon: ShoppingCart,
    badge: 1,
  }
] as const;

function isActivePath(pathname: string, href: string) {
  if (href === "/profile") {
    return pathname === href || pathname.startsWith(`${href}/`);
  }

  return pathname === href;
}

function useAvailablePoints() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { data: profile } = useGetProfileQuery(undefined, {
    skip: false,
  });

  return {
    availablePoints: profile?.points ?? null,
    referralPoints: profile?.referral_points ?? null,
    isAuthenticated,
  };
}

export function HeaderPointsPill({ className }: HeaderPointsPillProps) {
  const { availablePoints, isAuthenticated } = useAvailablePoints();
  const formattedPoints = useMemo(() => {
    if (availablePoints === null) {
      return null;
    }

    return new Intl.NumberFormat("en-US").format(availablePoints);
  }, [availablePoints]);

  if (!isAuthenticated || availablePoints === null || availablePoints <= 0) {
    return null;
  }

  return (
    <Link
      href="/cart"
      className={[
        "group inline-flex h-[2.55rem] w-full items-center justify-center gap-2 rounded-full border px-3 text-xs font-extrabold sm:h-[2.8rem] sm:px-4 sm:text-sm",
        "border-[rgba(241,89,34,0.18)] bg-white text-[var(--color-ink)]",
        "transition-all duration-300 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={`${availablePoints} تذكرة خصم متاحة للاستخدام`}
      title="تذاكر خصم متاحة للاستخدام"
    >
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/72 text-[var(--color-ink)] transition group-hover:bg-white/18 group-hover:text-[var(--color-paper)]">
        <WalletCards className="h-4 w-4" />
      </span>
      <span className="hidden leading-none min-[420px]:inline">
        {formattedPoints} تذكرة
      </span>
      <span className="leading-none min-[420px]:hidden">{formattedPoints}</span>
    </Link>
  );
}

export default function HeaderActionIcons({
  variant = "default",
}: HeaderActionIconsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { availablePoints } = useAvailablePoints();
  const { cartCount, wishlistCount } = useAppSelector(selectHeaderBadges);
  cartApi.useGetCartQuery(undefined, {
    skip: false,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  wishlistApi.useGetWishlistQuery(undefined, {
    skip: false,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const baseClasses =
    variant === "home"
      ? "border-[rgba(241,89,34,0.18)] bg-white text-[var(--color-ink)]"
      : "border-[rgba(241,89,34,0.18)] bg-white text-[var(--color-ink)]";
  const activeClasses =
    "border-[var(--color-accent)] bg-white text-[var(--color-accent)]";
  const hoverClasses =
    "transition-all duration-300 hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]";
  const currentQuery = searchParams.toString();
  const redirectPath = `${pathname}${currentQuery ? `?${currentQuery}` : ""}`;
  const badgeClassName =
    "absolute -left-[0.12rem] -top-[0.22rem] z-[2] inline-flex h-[1.05rem] min-w-[1.05rem] items-center justify-center rounded-full border-2 border-white bg-[#ee376a] px-1 text-[0.62rem] font-extrabold leading-none !text-white shadow-[0_6px_14px_rgba(238,55,106,0.28)] sm:h-[1.2rem] sm:min-w-[1.2rem] sm:text-[0.68rem]";
  const buildLoginHref = (destination: string) =>
    `${ROUTES.LOGIN}?${new URLSearchParams({
      redirect: destination,
    }).toString()}`;
  const loginHref = buildLoginHref(redirectPath);
  const formattedAvailablePoints =
    availablePoints === null
      ? null
      : new Intl.NumberFormat("en-US").format(availablePoints);
  const visibleActions = isAuthenticated
    ? actions
    : actions.filter(
        (action) => action.href !== "/profile" && action.href !== ROUTES.LOGOUT,
      );

  return (
    <div className="flex max-w-full shrink-0 flex-nowrap items-center justify-end gap-1.5 min-[390px]:gap-2 sm:gap-3">
      {!isAuthenticated ? (
        <Link
          href={loginHref}
          className={[
            "inline-flex h-[2.35rem] items-center justify-center rounded bg-[#ee376a] px-3 text-xs font-bold leading-none whitespace-nowrap text-[#fff] min-[390px]:h-[2.55rem] sm:h-[2.8rem] sm:px-5 sm:text-sm",
            "transition-all duration-300 hover:bg-[#d92f60]",
            pathname === ROUTES.LOGIN ? "bg-[#ee376a] text-[#fff]" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          تسجيل الدخول
        </Link>
      ) : null}

      {isAuthenticated && formattedAvailablePoints !== null ? (
        <Link
          href="/profile"
          className="group inline-flex h-[2.35rem] items-center justify-center gap-2 rounded border border-[rgba(241,89,34,0.18)] bg-transparent px-3 text-xs font-extrabold text-[#ee376a] transition-all duration-300 hover:border-[#ee376a] hover:bg-[#ee376a] hover:text-white min-[390px]:h-[2.55rem] sm:h-[2.8rem] sm:px-4 sm:text-sm"
          aria-label={`${formattedAvailablePoints} تذكرة خصم متاحة`}
          title="التذاكر المتاحة"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#ee376a]/10 text-[#ee376a] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:scale-105 group-hover:bg-white/20 group-hover:text-white">
            <Gift className="h-4 w-4" />
          </span>
          <span className="whitespace-nowrap">{formattedAvailablePoints} تذكرة</span>
        </Link>
      ) : null}

      {visibleActions.map((action) => {
        const Icon = action.icon;
        const isActive = isActivePath(pathname, action.href);
        const isAccentIcon =
          action.href === "/cart" || action.href === "/wishlist";
        const badgeValue: number | null = action.href === "/cart"
            ? cartCount
            : action.href === "/wishlist"
              ? wishlistCount
              : "badge" in action
                ? Number(action.badge)
                : null;
        const href = action.href;

        return (
          <Link
            key={action.href}
            href={href}
            className={[
              "relative inline-flex h-[2.35rem] w-[2.35rem] shrink-0 items-center justify-center  min-[390px]:h-[2.55rem] min-[390px]:w-[2.55rem] sm:h-[2.8rem] sm:w-[2.8rem] ",
              baseClasses,
              hoverClasses,
              isActive ? activeClasses : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-label={action.label}
          >
            {badgeValue ? (
              <span className={badgeClassName} aria-hidden="true">
                {badgeValue}
              </span>
            ) : null}
            <Icon
              className={[
                "h-4 w-4",
                isAccentIcon ? "text-[#ee376a]" : "",
                isActive ? "text-[var(--color-accent)]" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          </Link>
        );
      })}
    </div>
  );
}
